import React from 'react';
import { DeviceCreate } from '../../api/models/DeviceCreate';
import { DeviceSpecificFields } from '../common/DeviceSpecificFields';
import { useI18n } from '../../contexts/I18nContext';
import { useCreateDevice } from './createDevice/useCreateDevice';
import { DeviceIdentitySection } from './createDevice/DeviceIdentitySection';
import { DeviceTemplateSection } from './createDevice/DeviceTemplateSection';
import { DeviceFieldsSection } from './createDevice/DeviceFieldsSection';
import './CreateDeviceModal.css';

interface CreateDeviceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDeviceCreated: (device: DeviceCreate) => void;
}

/**
 * The "new device" dialog: identity first; once a type is chosen, the
 * template to apply and the fields of that type's schema. The state and
 * the rules live in `createDevice/`.
 */
export const CreateDeviceModal: React.FC<CreateDeviceModalProps> = ({ isOpen, onClose, onDeviceCreated }) => {
  const { t } = useI18n();
  const state = useCreateDevice({ isOpen, onClose, onDeviceCreated });

  if (!isOpen) return null;

  const { form, errors, submitting } = state;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="create-device-modal create-device-modal-v2" data-testid="modal-create-device" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="header-title-section">
            <h2>{t('devices.addDevice')}</h2>
          </div>
          <button className="close-button" data-testid="btn-modal-close" onClick={onClose} aria-label={t('common.close')}>×</button>
        </div>

        {errors.submit && (
          <div className="error-banner">
            <span>{errors.submit}</span>
            <button type="button" onClick={state.dismissSubmitError} aria-label={t('common.dismissError')}>×</button>
          </div>
        )}

        <form onSubmit={state.submit}>
          <div className="modal-content device-sections">
            <DeviceIdentitySection state={state} />

            {!form.deviceType ? (
              <div className="device-section">
                <p className="template-hint is-centered">{t('devices.selectHardwareHint')}</p>
              </div>
            ) : state.schemaLoading ? (
              <div className="device-section">
                <p className="template-hint is-centered">{t('devices.loadingSchema', { type: form.deviceType })}</p>
              </div>
            ) : (
              <>
                <DeviceTemplateSection state={state} />
                <DeviceFieldsSection state={state} />
                {state.customFields.length > 0 && (
                  <div className="device-section">
                    <DeviceSpecificFields fields={state.customFields} onChange={state.setCustomFields} isEditing />
                  </div>
                )}
              </>
            )}
          </div>

          <div className="modal-actions">
            <div className="action-group-left">
              <button type="button" onClick={onClose} className="btn btn-secondary" data-testid="btn-cancel" disabled={submitting}>
                {t('common.cancel')}
              </button>
            </div>
            <div className="action-group-right">
              <button type="submit" className="btn btn-primary" data-testid="btn-submit" disabled={submitting}>
                {submitting ? t('devices.create.submitting') : t('devices.create.submit')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
